import { agentShortName, type ProjectMemberView, type TelegramStatus } from "@baton/contracts";
import type { DecryptedProjectGraph, DecryptedProjectInvitation, DecryptedProjectSummary } from "./projects-view.js";
import { layoutPositions, type BatonNode } from "./flow/model.js";
import { buildOptionsFor, type FlowViewer } from "./flow/flow-canvas.js";
import { useFlowActions } from "./flow/nodes.js";
import type { PositionChange } from "./hooks/use-canvas-positions.js";
import { PeopleList, type ProjectPeopleState } from "./people-list.js";

export interface FlowSidebarProps {
  userHandle: string;
  projects: DecryptedProjectSummary[];
  invitations: DecryptedProjectInvitation[];
  selectedProjectId: string | null;
  selectProject(id: string): void;
  graph: DecryptedProjectGraph | null;
  viewer: FlowViewer;
  loading: boolean;
  realtime: "connected" | "reconnecting" | "unavailable" | "connecting";
  listView: boolean;
  setListView(value: boolean): void;
  selectedNode: BatonNode | null;
  linkableRepositoryCount: number;
  onNewProject(): void;
  onTelegram(): void;
  onSetup(): void;
  onHandover(): void;
  onMessages(): void;
  setupRemaining: number;
  people: ProjectPeopleState;
  telegram: TelegramStatus | null;
  onAcceptInvitation(projectId: string): void;
  onTidy(positions: PositionChange[]): void;
  onDemoPacket?: (() => void) | undefined;
  onRevokeMember(member: ProjectMemberView): void;
  onChangeRole(member: ProjectMemberView, role: "member" | "admin"): void;
  onLock(): void;
}

/** Daily actions stay visible; administration and canvas tools appear on request. */
export function FlowSidebar(props: FlowSidebarProps) {
  const actions = useFlowActions();
  const graph = props.graph;
  return <aside className="sidebar tools" data-testid="flow-sidebar">
    <div className="sidebar-head">
      <div className="wordmark small">baton<span>:</span></div>
      <p className="signed-in">@{props.userHandle}</p>
      {props.realtime !== "connected" && <p className={`sidebar-status realtime-${props.realtime}`}><span className="pulse" />{props.loading ? "Connecting…" : props.realtime}</p>}
    </div>
    <section className="sidebar-section">
      <p className="eyebrow">Your project</p>
      {props.projects.length > 0 && <select aria-label="Project" value={props.selectedProjectId ?? ""} onChange={(event) => props.selectProject(event.target.value)}>
        {props.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
      </select>}
      {graph && <p className="sidebar-project-meta muted">{graph.repositories.length} repositories · {graph.members.filter((member) => member.state === "active").length} people</p>}
      {props.invitations.length > 0 && <ul className="invite-list compact">
        {props.invitations.map((invitation) => <li key={invitation.projectId}>
          <span><strong>{invitation.name}</strong><br /><small className="muted">from {invitation.invitedByHandle}</small></span>
          <button type="button" onClick={() => props.onAcceptInvitation(invitation.projectId)}>Accept</button>
        </li>)}
      </ul>}
    </section>
    {graph && <section className="sidebar-section daily-actions" aria-label="Project actions">
      <button type="button" className="primary" onClick={props.onHandover}>Handover <span aria-hidden="true">↗</span></button>
      <button type="button" className="sidebar-button" onClick={props.onMessages}>Messages</button>
    </section>}
    <section className="sidebar-section sidebar-telegram" data-testid="sidebar-telegram">
      <button type="button" className="sidebar-button sidebar-telegram-row" onClick={props.onTelegram}>
        <span>{props.telegram?.linked ? "Telegram" : "Join Telegram"}</span>
        <span className={`sidebar-telegram-state${props.telegram?.linked ? " linked" : ""}`} data-testid="sidebar-telegram-state">
          {props.telegram === null ? "checking…" : !props.telegram.available ? "unavailable" : props.telegram.linked ? "linked" : "not linked"}
        </span>
      </button>
    </section>
    <details className="sidebar-section sidebar-manage" data-testid="project-management">
      <summary>{graph ? "Manage project" : "Project options"}</summary>
      <div className="sidebar-section">
        <button type="button" className="sidebar-button" onClick={props.onNewProject}>New project</button>
        {graph && <>
          <div className="flow-palette" data-testid="flow-palette" role="toolbar" aria-label="Add to the canvas">
            {actions.permissions.isAdmin && <button type="button" onClick={() => actions.addRepository()}>+ Repository</button>}
            {actions.permissions.isAdmin && <button type="button" onClick={() => actions.invite()}>Invite</button>}
            <button type="button" onClick={() => actions.createAgent()}>+ Agent</button>
          </div>
          <p className="muted compact">Your general agent is assigned automatically. Extra agents are optional.</p>
          <h4>Members</h4>
          <ul className="sidebar-members">
            {graph.members.filter((member) => member.state !== "revoked").map((member) => <li key={member.userId}>
              <span>@{member.handle}<small className="muted"> {member.state === "active" ? member.role : "invited"}</small></span>
              {actions.permissions.isAdmin && member.role !== "owner" && member.userId !== props.viewer.userId && <div className="member-controls">
                {member.state === "active" && <select aria-label={`Role for ${member.handle}`} value={member.role} onChange={(event) => props.onChangeRole(member, event.target.value as "member" | "admin")}>
                  <option value="member">Member</option><option value="admin">Admin</option>
                </select>}
                <button type="button" className="link danger" onClick={() => props.onRevokeMember(member)}>remove</button>
              </div>}
            </li>)}
          </ul>
          {actions.permissions.isAdmin && <details className="sidebar-people" data-testid="sidebar-people">
            <summary>People <span className="sidebar-count" data-testid="sidebar-people-count">{props.people.rows.length}</span></summary>
            {props.people.error && <p className="error compact">{props.people.error}</p>}
            <PeopleList rows={props.people.rows} variant="compact" canInvite={props.people.canInvite} role="member" busyUserId={props.people.busyUserId} outcomes={props.people.outcomes} onInvite={(person) => void props.people.invite(person, "member")} emptyLabel="Link a repository to find its collaborators." />
          </details>}
        </>}
        <button type="button" className="sidebar-button sidebar-setup" data-testid="sidebar-setup" onClick={props.onSetup}>Set up {props.setupRemaining > 0 && <span className="sidebar-count" data-testid="sidebar-setup-count">{props.setupRemaining}</span>}</button>
      </div>
    </details>
    {graph && <details className="sidebar-section" data-testid="canvas-options">
      <summary>Canvas options</summary>
      <div className="flow-mode" role="group" aria-label="Graph presentation">
        <button type="button" className={props.listView ? "" : "active"} aria-pressed={!props.listView} onClick={() => props.setListView(false)}>Canvas</button>
        <button type="button" className={props.listView ? "active" : ""} aria-pressed={props.listView} onClick={() => props.setListView(true)}>List</button>
      </div>
      <button type="button" className="sidebar-button" onClick={() => props.onTidy(layoutPositions(graph, buildOptionsFor(props.viewer)))}>Tidy up</button>
      <p className="muted compact">Square blocks are GitHub repositories. Rounded blocks are agents. Drag between agents to open a conversation.</p>
      {props.onDemoPacket && <button type="button" className="sidebar-button" onClick={props.onDemoPacket}>Play demo packet</button>}
    </details>}
    {props.selectedNode && <section className="sidebar-section" data-testid="flow-selection"><SelectionDetail node={props.selectedNode} /></section>}
    <div className="sidebar-foot"><button className="lock" onClick={props.onLock}>Lock & clear memory</button></div>
  </aside>;
}

function SelectionDetail({ node }: { node: BatonNode }) {
  const actions = useFlowActions();
  const data = node.data;
  if (data.kind === "repository") return <div className="flow-selection">
    <strong>{data.ownerHandle}/{data.name}</strong><span className="muted">git repository · {data.defaultBranch}</span>
  </div>;
  return <div className="flow-selection">
    <strong>{agentShortName(data, data.ownerHandle)}</strong><span className="muted">agent of @{data.ownerHandle ?? "unknown"}</span>
    <div className="flow-selection-actions">
      {!data.isViewerGeneral && <button type="button" onClick={() => actions.chat({ agentId: data.agentId, handle: data.handle })}>Chat</button>}
      {data.ownedByViewer && <button type="button" onClick={() => actions.connect({ agentId: data.agentId, handle: data.handle })}>Connect</button>}
    </div>
  </div>;
}
